import { defineComponent as _defineComponent } from 'vue'
import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    onUpdated,
    ref,
    watch,
    withDefaults,
} from "vue";
import TrashSvg from "@/assets/icons/trash.svg";

/** Высота поля ввода как у BaseInput .base-input__input (is-size-*) */

export default /*#__PURE__*/_defineComponent({
  __name: 'BaseTextarea',
  props: {
    value: null,
    rows: { default: 2 },
    autoresize: { type: Boolean, default: true },
    size: { default: 'large' },
    clearable: { type: Boolean },
    clearableType: { default: 'inner' },
    view: { default: 'outlined' },
    isValid: { type: Boolean },
    readonly: { type: Boolean },
    disabled: { type: Boolean },
    resetRowHeight: { type: Boolean },
    placeholder: null
  },
  emits: ['update:value'],
  setup(__props: any, { emit: emits }) {

const props = __props as { value: string, rows: number, autoresize: boolean, size: 'small' | 'medium' | 'large', clearable?: boolean, clearableType: 'inner' | 'out', view: 'border-bottom' | 'outlined', isValid?: boolean, readonly?: boolean, disabled?: boolean, resetRowHeight?: boolean, placeholder?: string }

const INPUT_MIN_HEIGHT_PX: Record<'small' | 'medium' | 'large', number> = {
    small: 18,
    medium: 24,
    large: 34,
};

const ROW_FALLBACK_PX = 18;




const rootEl = ref<HTMLElement | null>(null);
const textarea = ref<HTMLTextAreaElement | null>(null);

let intersectionObserver: IntersectionObserver | null = null;

const getClasses = computed(() => [
    props.view === 'outlined' && 'is-outlined',
    props.view === 'border-bottom' && 'is-border-bottom',
    props.clearable && 'is-clearable',
    props.isValid && 'is-valid',
    props.readonly && 'is-readonly',
    props.disabled && 'is-disabled',
    props.autoresize && 'is-autoresize',
    props.autoresize && `is-size-${props.size}`,
]);

const isShowClearable = computed<boolean>(() =>
    props.clearable && !!props.value && !props.readonly && !props.disabled,
);

const textareaRowsAttr = computed(() => (props.autoresize ? 1 : props.rows));

const minHeightPx = computed(() => {
    if (!props.autoresize) {
        return props.rows * ROW_FALLBACK_PX;
    }
    return INPUT_MIN_HEIGHT_PX[props.size];
});

const fieldInlineStyle = computed(() => ({
    minHeight: `${minHeightPx.value}px`,
}));

/**
 * Обязательно сбрасывать height в auto перед scrollHeight — иначе при удалении текста высота не уменьшается.
 */
const calculateHeight = (): void => {
    const el = textarea.value;
    if (!el) return;

    const minH = props.autoresize
        ? INPUT_MIN_HEIGHT_PX[props.size]
        : props.rows * ROW_FALLBACK_PX;

    el.style.minHeight = `${minH}px`;
    el.style.height = 'auto';
    el.style.height = `${Math.max(minH, el.scrollHeight)}px`;
};

let heightFramePending = false;

const scheduleHeight = (): void => {
    if (heightFramePending) return;
    heightFramePending = true;
    nextTick(() => {
        requestAnimationFrame(() => {
            heightFramePending = false;
            calculateHeight();
        });
    });
};

/** Сразу при фокусе — длинный текст может быть с неверной высотой до активации поля */
const resizeOnFocus = (): void => {
    if (props.disabled) return;
    calculateHeight();
    requestAnimationFrame(() => {
        calculateHeight();
    });
};

const handleInput = (e: Event): void => {
    const target = e.target as HTMLTextAreaElement;
    if (!target) {
        handleClear();
        return;
    }
    emits('update:value', target.value);
    scheduleHeight();
};

const handleClear = (): void => {
    emits('update:value', '');
    scheduleHeight();
};

watch(() => props.value, scheduleHeight);

watch(
    () => [props.autoresize, props.rows, props.size] as const,
    scheduleHeight,
);

const bindVisibilityObserver = (): void => {
    if (typeof IntersectionObserver === 'undefined') return;
    const el = rootEl.value;
    if (!el) return;

    intersectionObserver?.disconnect();
    intersectionObserver = new IntersectionObserver(
        (entries) => {
            for (const entry of entries) {
                if (entry.isIntersecting) {
                    scheduleHeight();
                    break;
                }
            }
        },
        { root: null, rootMargin: '0px', threshold: 0 },
    );
    intersectionObserver.observe(el);
};

onMounted(() => {
    scheduleHeight();
    nextTick(() => {
        scheduleHeight();
        bindVisibilityObserver();
    });
});

/** keep-alive: снова в DOM */
onActivated(() => {
    scheduleHeight();
    nextTick(scheduleHeight);
});

onBeforeUnmount(() => {
    intersectionObserver?.disconnect();
    intersectionObserver = null;
});

/** После v-if / обновления дерева — пересчитать scrollHeight (coalesce в одном rAF) */
onUpdated(() => {
    scheduleHeight();
});

return { __sfc: true,INPUT_MIN_HEIGHT_PX, ROW_FALLBACK_PX, props, emits, rootEl, textarea, intersectionObserver, getClasses, isShowClearable, textareaRowsAttr, minHeightPx, fieldInlineStyle, calculateHeight, heightFramePending, scheduleHeight, resizeOnFocus, handleInput, handleClear, bindVisibilityObserver, TrashSvg }
}

})